import * as React from 'react';
import { useRef } from 'react';
import styled from 'styled-components';
import { colors } from '../colors/color';
import { formFieldValidationHelper } from '../formField/FormField';
import { Icon, iconName, IconSize } from '../icons/Icons';
import { InputMessage } from '../InputMessage';
import { InputSize, InputStyleMode, textInputPadding } from '../inputs/TextInput';
import { TextInputWithSave } from '../inputs/TextInputWithSave';
import { H1, H2, H3, H4, HeaderProps } from '../typography/Heading';
import { Flex, FlexItem } from './FlexHelpers';

type EditableTextProps = {
    ariaLabel: string,
    value: string,
    placeholder: string,
    inErrorState?: boolean,
    infoMessage?: string,
    errorMessage?: string,
    disabled?: boolean,
    reserveMessageSpace?: boolean,
    leadingIcon?:iconName,
    leadingIconSize?:IconSize,
    validator?: (currentValue: string) => { valid: true; } | { valid: false, errorMessage: string; },
    onChange?: (value) => void,
    onCancel?: (cancelledValue: string, restoredValue: string) => void,
    onSave?: (value: string, isValid: boolean) => void,
    onSaveWithReturnValue?: (value: string, isValid: boolean) => Promise<boolean>,
    styleMode?: InputStyleMode;
    fontSize?: 14 | 16,
    inputSize?: InputSize,
    hideHoverEditButton?: boolean,
    justifyContent?: 'flex-start' | 'space-between';
};
export function EditableText (props: EditableTextProps): JSX.Element
{
    const [inEdit, setInEdit] = React.useState(false);
    const [currentValue, setCurrentValue] = React.useState(props.value);
    const inputRef = useRef<HTMLInputElement>(null);
    const resolvedInputSize = props.inputSize || 'auto';

    React.useEffect(() =>
    {
        // Sæt fokus på inputfeltet, når `inEdit` bliver `true`
        if (inEdit && inputRef.current)
        {
            inputRef.current.focus();
        }
    }, [inEdit]);

    React.useEffect(() =>
    {
        setCurrentValue(props.value);
    }, [props.value]);

    function handleChange (value: string)
    {
        setCurrentValue(value);
        if (props.onChange)
        {
            props.onChange(value);
        }
    }

    function handleCancel (cancelledValue: string, restoredValue: string)
    {
        setCurrentValue(restoredValue);
        if (props.onCancel)
        {
            props.onCancel(cancelledValue, restoredValue);
        }
        setInEdit(false);
    }

    async function handleSave (newValue: string)
    {
        setCurrentValue(props.value);

        if (props.onSaveWithReturnValue)
        {
            const valid = await props.onSaveWithReturnValue?.(newValue, !inErrorState);
            if (valid)
                setInEdit(false);
        }

        else if (props.onSave)
        {
            props.onSave?.(newValue, !inErrorState);
            setInEdit(false);
        }
    }

    const { inErrorState, message } = formFieldValidationHelper(currentValue, props.infoMessage, props.errorMessage, props.validator);
    const { paddingVertical, paddingHorizontal } = textInputPadding(props.fontSize);
    const hideHoverButton = props.disabled===true || props.hideHoverEditButton === true;

    const headerProps:HeaderProps = {
        "aria-label": props.ariaLabel,
        color: inErrorState 
            ?   'error'
                : props.placeholder && props.value === '' 
                    ? 'placeholder' 
                    : undefined,
        

    }
    const readOnlyValue = props.value === "" ? props.placeholder : props.value;

    return (
        <FlexItem style={{flexGrow:1, width:'100%'}}>
            <Flex alignItems='center'>
                {props.leadingIcon && 
                    <Icon name={props.leadingIcon} 
                        size={props.leadingIconSize}
                        color={inErrorState ? colors.icon.error : undefined} />
                }
                
                <EditableInputWrapper fontSize={props.fontSize || 16} paddingVertical={paddingVertical} paddingHorizontal={paddingHorizontal}>
                    {inEdit &&
                        <Flex flexDirection='row' alignItems="center">

                            <FlexItem flexGrow='1'>
                                <TextInputWithSave
                                    ariaLabel={props.ariaLabel}
                                    innerRef={inputRef}
                                    value={props.value}
                                    onChange={handleChange}
                                    onCancel={handleCancel}
                                    onSave={handleSave}
                                    onSaveNoChange={() => setInEdit(false)}
                                    inErrorState={inErrorState}
                                    disabled={props.disabled}
                                    styleMode={props.styleMode}
                                    size={resolvedInputSize}
                                    placeholder={props.placeholder}
                                    fontSize={props.fontSize} />
                            </FlexItem>
                        </Flex>
                    }
                    {!inEdit &&
                        <Flex alignItems="center" justifyContent={props.justifyContent || 'flex-start'} onClick={() => !props.disabled && setInEdit(true)} >

                            {(props.styleMode === undefined || props.styleMode === 'default') && 
                                <span 
                                    aria-label={props.ariaLabel} 
                                    style={{ 
                                        color: props.placeholder && props.value === ""
                                            ? colors.text.inactive 
                                            : undefined
                                    }}>
                                    {readOnlyValue}
                                </span>
                            }
                            {props.styleMode === 'H1' && <H1 {...headerProps}>{readOnlyValue}</H1>}
                            {props.styleMode === 'H2' && <H2 {...headerProps}>{readOnlyValue}</H2>}
                            {props.styleMode === 'H3' && <H3 {...headerProps}>{readOnlyValue}</H3>}
                            {props.styleMode === 'H4' && <H4 {...headerProps}>{readOnlyValue}</H4>}
                            {!hideHoverButton &&
                                <EditableInputFadingButton aria-label={`rediger ${props.ariaLabel}`}>
                                    <Icon name='interface_actions_edit' />
                                </EditableInputFadingButton>
                            }
                        </Flex>
                    }
                </EditableInputWrapper>
            </Flex>

            <div style={{ minHeight: props.reserveMessageSpace ? '30px' : undefined }}>
                {message !== undefined && message !== '' &&
                    <InputMessage inErrorState={inErrorState}>
                        {message}
                    </InputMessage>
                }
            </div>
        </FlexItem>
    );
}

export const EditableInputFadingButton = styled.button`
    border:none;
    padding: 0;
    opacity: 0.2;
    transition: opacity 0.2s ease;
    background: transparent;

    &:hover{
        cursor: pointer;
    }

`;


export const EditableInputWrapper = styled.div<{fontSize:number, paddingVertical:number, paddingHorizontal:number}>`

    flex-grow: 1;
    min-width: 0;

    & h1, h2, h3, h4{
        border: 1px solid transparent;
        padding: 10px 15px;
        white-space: pre-wrap;
        overflow-wrap: anywhere;
        min-width: 0;
    }

    & span{
        border: 1px solid transparent;
        padding: ${props => props.paddingVertical}px ${props => props.paddingHorizontal}px;
        font-size: ${props => props.fontSize}px;
        font-weight:400;
        white-space: pre-wrap;
        overflow-wrap: anywhere;
        min-width: 0;
    }

    &:hover{
        background: ${colors.editableInput.backgroundHover};
        transition: background 0.2s ease;

        & ${EditableInputFadingButton}{
            opacity: 1;
        }
    }
    
`;